import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Users, UserCheck, Clock, UserX, Search, Plus, Eye, Pencil, CheckCircle, XCircle, Power, RotateCcw, ChevronLeft, ChevronRight, X, Loader2, FileText, ExternalLink, RefreshCw, Trash2 } from "lucide-react";
import { getMembers, getMemberById, createMember, updateMember, updateMemberStatus, approveMember, rejectMember, getAllMemberMasters, uploadMemberDocument, getMemberDocuments, deleteMemberDocument } from "../../api/membersApi";
const getErrorMessage = (error) => error?.response?.data?.message || error?.message || "Something went wrong.";

const unwrapMembers = (response) => {
    const root = response?.data ?? response;
    const payload = root?.data ?? root;
    return {
        items: Array.isArray(payload?.items)
            ? payload.items
            : Array.isArray(payload)
                ? payload
                : [],
        pagination: payload?.pagination ?? {},
    };
};

const getDocumentUrl = (doc) => {
    const rawUrl = doc?.document_url || doc?.file_url || doc?.url || doc?.file_path || doc?.document_path || "";
    if (!rawUrl) return "";
    if (/^https?:\/\//i.test(rawUrl)) {
        return rawUrl;
    }
    return `https://praneeshinfotech.com/kongudevapi/${String(rawUrl).replace(/^\/+/, "")}`;
};

const isImageDocument = (doc) => {
    const type = String(doc?.mime_type || doc?.file_type || doc?.document_mime_type || "").toLowerCase();
    const name = String(doc?.original_name || doc?.file_name || doc?.filename || doc?.file_path || "").toLowerCase();
    return (type.startsWith("image/") || /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(name));
};

const unwrapSingle = (response) => {
    const root = response?.data ?? response;
    const payload = root?.data ?? root;
    return (payload?.member ?? payload?.item ?? payload?.result ?? payload);
};

const unwrapDocuments = (response) => {
    const root = response?.data ?? response;
    const payload = root?.data ?? root;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.documents)) return payload.documents;
    return [];
};

const normalizeMember = (member = {}) => ({
    ...member,
    rfId: member.rfId ?? member.rf_id ?? member.id,
    name: member.name ?? "",
    mobile_number: member.mobile_number ?? "",
    approval_status: String(
        member.approval_status ?? "pending"
    ).toLowerCase(),
    status: String(member.status ?? "active").toLowerCase(),
});

const dateOnly = (value) => {
    if (!value) return "—";
    const date = String(value).slice(0, 10);
    if (date === "0000-00-00") return "—";
    const [year, month, day] = date.split("-");
    if (!year || !month || !day) return "—";
    return `${day}-${month}-${year}`;
};

const formatDateTime = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    });
};

const getUserRfId = () => {
    try {
        const user = JSON.parse(localStorage.getItem("loggedInUser") || "{}");
        return (user?.rfId ?? user?.rf_id ?? user?.user?.rfId ?? user?.user?.rf_id ?? null);
    } catch {
        return null;
    }
};

const getMasterLabel = (member, field) => {
    const en = member?.[`${field}_name_en`];
    const ta = member?.[`${field}_name_ta`];
    if (en && ta) return `${en} / ${ta}`;
    return en || ta || "—";
};

function Badge({ children, color = "gray" }) {
    const styles = {
        green: "bg-green-50 text-green-700",
        amber: "bg-amber-50 text-amber-700",
        red: "bg-red-50 text-red-700",
        blue: "bg-blue-50 text-blue-700",
        gray: "bg-gray-100 text-gray-600",
    };
    return (
        <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${styles[color] || styles.gray}`}>
            {children}
        </span>
    );
}

function SummaryCard({ title, value, icon: Icon, color }) {
    const colors = {
        blue: "bg-blue-50 text-blue-600",
        green: "bg-green-50 text-green-600",
        amber: "bg-amber-50 text-amber-600",
        red: "bg-red-50 text-red-600",
    };
    return (
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm text-gray-500">{title}</p>
                    <h2 className="mt-2 text-2xl font-bold text-gray-800">{value}</h2>
                </div>
                <div className={`rounded-xl p-3 ${colors[color]}`}>
                    <Icon size={23} />
                </div>
            </div>
        </div>
    );
}

function DetailItem({ label, value }) {
    return (
        <div className="min-w-0 rounded-lg border border-gray-100 bg-gray-50 p-3">
            <p className="text-[11px] font-medium text-gray-400">{label}</p>
            <p className="mt-1 break-words text-sm font-semibold text-gray-700">{value === null || value === undefined || value === "" ? "—" : String(value)}</p>
        </div>
    );
}

function DetailSection({ title, children }) {
    return (
        <section className="border-t border-gray-100 pt-5">
            <h3 className="mb-4 text-sm font-bold text-gray-800">{title}</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
        </section>
    );
}

function Popup({ popup, onClose }) {
    if (!popup) return null;
    const confirm = popup.type === "confirm";
    const busy = Boolean(popup.loading);
    return (
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
                <div className="mb-4 flex justify-center">
                    {popup.type === "success" ? (
                        <CheckCircle size={36} className="text-green-600" />
                    ) : confirm ? (
                        <Power size={36} className="text-amber-600" />
                    ) : (
                        <XCircle size={36} className="text-red-600" />
                    )}
                </div>
                <h3 className="text-center text-lg font-bold text-gray-800">{popup.title}</h3>
                <p className="mt-2 whitespace-pre-line text-center text-sm text-gray-500">{popup.message}</p>
                <div className="mt-6 flex justify-center gap-3">
                    {confirm ? (
                        <>
                            <button type="button" onClick={onClose} disabled={busy}
                                className="rounded-lg border px-5 py-2.5 text-sm disabled:cursor-not-allowed 
                            disabled:opacity-50">Cancel</button>
                            <button type="button" onClick={popup.onConfirm} disabled={busy}
                                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white 
                            disabled:cursor-not-allowed disabled:opacity-60">
                                {busy && (<Loader2 size={15} className="animate-spin" />)}
                                {busy ? "Updating..." : "Confirm"}
                            </button>
                        </>
                    ) : (
                        <button type="button" onClick={onClose} className="rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-semibold 
                        text-white">OK</button>
                    )}
                </div>
            </div>
        </div>
    );
}

function MemberViewModal({ member, documents, loadingDocuments, onClose, onEdit, onApprove, onReject, onDeleteDocument }) {
    const approval = member.approval_status;
    const accountStatus = member.status;
    const profileFields = [
        ["Member ID", `KVM${String(member.rfId).padStart(4, "0")}`],
        ["Profile Created By", getMasterLabel(member, "profile_created_by")],
        ["Name", member.name],
        ["Gender", getMasterLabel(member, "gender")],
        ["Date of Birth", dateOnly(member.date_of_birth)],
        ["Time of Birth", formatDateTime(member.time_of_birth)],
        ["Place of Birth", member.place_of_birth],
        ["Mobile Number", member.mobile_number],
        ["Alternate Mobile", member.alternate_mobile_number],
        ["Marital Status", getMasterLabel(member, "marital_status")],
    ];

    const addressFields = [
        ["Address Line 1", member.address_line1],
        ["Address Line 2", member.address_line2],
        ["City", getMasterLabel(member, "city")],
        ["District", getMasterLabel(member, "district")],
        ["State", getMasterLabel(member, "state")],
        ["PIN Code", member.pin_code],
        ["Native Place", member.native_place],
    ];

    const familyFields = [
        ["Father Name", member.father_name],
        ["Mother Name", member.mother_name],
        ["Father Koottam", getMasterLabel(member, "father_family_koottam")],
        ["Father Family Temple", getMasterLabel(member, "father_family_temple")],
        ["Father Temple Place", getMasterLabel(member, "father_family_temple_place")],
        ["Mother Koottam", getMasterLabel(member, "mother_family_koottam")],
        ["Mother Family Temple", getMasterLabel(member, "mother_family_temple")],
        ["Mother Temple Place", getMasterLabel(member, "mother_family_temple_place")],
        ["Number of Siblings", member.number_of_siblings],
        ["Married Siblings", member.married_siblings],
        ["Number of Children", member.number_of_children],
        ["Family Assets", member.family_assets],
        ["Family Type", getMasterLabel(member, "family_type")],
    ];

    const educationFields = [
        ["Highest Education", getMasterLabel(member, "highest_education")],
        ["Occupation", getMasterLabel(member, "occupation")],
        ["Designation", getMasterLabel(member, "designation")],
        ["Company Name", member.company_name],
        ["Monthly Income", member.monthly_income],
        ["Work Location", member.work_location],
    ];

    const physicalFields = [
        ["Height", getMasterLabel(member, "height")],
        ["Weight", getMasterLabel(member, "weight")],
        ["Complexion", getMasterLabel(member, "color")],
        ["Blood Group", getMasterLabel(member, "blood_group")],
    ];

    const horoscopeFields = [
        ["Rasi", getMasterLabel(member, "rasi")],
        ["Nakshatram", getMasterLabel(member, "nakshatram")],
        ["Paadam", getMasterLabel(member, "paadam")],
        ["Dhosam", getMasterLabel(member, "dhosam")],
    ];

    const referralFields = [
        ["Referred By", member.referred_by],
        ["Referrer Mobile", member.referred_by_mobile],
    ];

    const systemFields = [
        ["OTP Verification", member.otp_verified_status],
        ["Registration Source", member.registration_source],
        ["Branch ID", member.branch_id],
        ["Created By", member.created_by],
        ["Updated By", member.updated_by],
        ["Created At", formatDateTime(member.created_at)],
        ["Updated At", formatDateTime(member.updated_at)],
        ["Approved By", member.approved_by_1],
        ["Approved At", formatDateTime(member.approved_at_1)],
    ];

    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-2 sm:p-5">
            <div className="flex max-h-[96vh] w-full max-w-7xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
                <div className="flex items-center justify-between border-b p-5">
                    <div>
                        <h2 className="text-lg font-bold text-gray-800">Member Profile Details</h2>
                        <p className="mt-1 text-xs text-gray-500">{`Member ID: KVM${String(member.rfId).padStart(4, "0")}`}</p>
                    </div>
                    <button onClick={onClose} className="rounded-lg p-2 hover:bg-gray-100">
                        <X size={20} />
                    </button>
                </div>
                <div className="flex-1 space-y-6 overflow-y-auto p-5">
                    <div className="flex flex-col gap-4 rounded-xl bg-blue-50 p-5 sm:flex-row sm:items-center">
                        <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full bg-blue-100 text-2xl font-bold text-blue-700">
                            {member.profile_picture_path ? (
                                <img src={member.profile_picture_path} alt={member.name} className="h-full w-full object-cover" />
                            ) : (
                                member.name?.charAt(0) || "M"
                            )}
                        </div>
                        <div className="min-w-0 flex-1">
                            <h3 className="text-xl font-bold text-gray-800">{member.name}</h3>
                            <p className="mt-1 text-sm text-gray-500">{member.mobile_number}</p>
                            <div className="mt-3 flex flex-wrap gap-2">
                                <Badge color={approval === "approved" ? "green" : approval === "rejected" ? "red" : "amber"}>
                                    Approval Status : {approval}
                                </Badge>
                                <Badge color={accountStatus === "active" ? "green" : "gray"}>
                                    Account Status : {accountStatus}
                                </Badge>
                                <Badge color="blue">
                                    OTP Verified Status : {member.otp_verified_status || "—"}
                                </Badge>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {approval !== "approved" && (
                                <button onClick={() => onApprove(member)} className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2.5 text-xs font-semibold text-white hover:bg-green-700">
                                    <CheckCircle size={15} />
                                    Approve
                                </button>
                            )}
                            {approval !== "rejected" && (
                                <button onClick={() => onReject(member)} className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-xs font-semibold text-white hover:bg-red-700">
                                    <XCircle size={15} />
                                    Reject
                                </button>
                            )}
                            <button onClick={() => onEdit(member)} className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-xs font-semibold text-white">
                                <Pencil size={15} />
                                Edit
                            </button>
                        </div>
                    </div>
                    {member.reject_reason && (
                        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                            <p className="text-xs font-bold text-red-700">Rejection Reason</p>
                            <p className="mt-1 text-sm text-red-600">{member.reject_reason}</p>
                        </div>
                    )}
                    <DetailSection title="Personal Information">
                        {profileFields.map(([label, value]) => (
                            <DetailItem key={label} label={label} value={value} />
                        ))}
                    </DetailSection>
                    <DetailSection title="Address Information">
                        {addressFields.map(([label, value]) => (
                            <DetailItem key={label} label={label} value={value} />
                        ))}
                    </DetailSection>
                    <DetailSection title="Family Information">
                        {familyFields.map(([label, value]) => (
                            <DetailItem key={label} label={label} value={value} />
                        ))}
                    </DetailSection>
                    <DetailSection title="Education & Career">
                        {educationFields.map(([label, value]) => (
                            <DetailItem key={label} label={label} value={value} />
                        ))}
                    </DetailSection>
                    <DetailSection title="Physical Information">
                        {physicalFields.map(([label, value]) => (
                            <DetailItem key={label} label={label} value={value} />
                        ))}
                    </DetailSection>
                    <DetailSection title="Horoscope Information">
                        {horoscopeFields.map(([label, value]) => (
                            <DetailItem key={label} label={label} value={value} />
                        ))}
                    </DetailSection>
                    <DetailSection title="Referral Information">
                        {referralFields.map(([label, value]) => (
                            <DetailItem key={label} label={label} value={value} />
                        ))}
                    </DetailSection>
                    <DetailSection title="Registration & Approval Information">
                        {systemFields.map(([label, value]) => (
                            <DetailItem key={label} label={label} value={value} />
                        ))}
                    </DetailSection>

                    <section className="border-t border-gray-100 pt-5">
                        <h3 className="mb-4 text-sm font-bold text-gray-800">Uploaded Documents</h3>
                        {loadingDocuments ? (
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                <Loader2 size={17} className="animate-spin" />
                                Loading documents...
                            </div>
                        ) : documents.length > 0 ? (
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                {documents.map((doc, index) => {
                                    const url = doc.document_url || doc.file_url || doc.url || doc.file_path;
                                    const isImage = String(
                                        doc.mime_type || doc.document_mime_type || ""
                                    ).startsWith("image/");
                                    const documentName = doc.original_file_name || doc.original_name || doc.file_name || doc.document_type || "Document";
                                    return (
                                        <div key={doc.rfId || doc.id || index} className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                                            <div className="flex h-44 items-center justify-center bg-gray-50">
                                                {isImage && url ? (
                                                    <img src={url} alt={documentName} className="h-full w-full object-contain" />
                                                ) : (
                                                    <div className="flex flex-col items-center gap-2 text-gray-400">
                                                        <FileText size={42} className="text-red-500" />
                                                        <span className="text-sm font-semibold">PDF / Document</span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex items-center justify-between gap-2 border-t p-3">
                                                <div className="min-w-0 flex-1">
                                                    <p className="mt-1 text-    xs capitalize text-gray-400">
                                                        {String(
                                                            doc.document_type || "document"
                                                        ).replaceAll("_", " ")}
                                                    </p>
                                                </div>
                                                <div className="flex shrink-0 items-center gap-1">
                                                    {url && (
                                                        <a href={url} target="_blank" rel="noreferrer" title="View document" className="rounded-lg p-2 text-blue-600 hover:bg-blue-50">
                                                            <ExternalLink size={17} />
                                                        </a>
                                                    )}
                                                    <button type="button" onClick={() => onDeleteDocument(doc)} title="Delete document" className="rounded-lg p-2 text-red-600 hover:bg-red-50">
                                                        <Trash2 size={17} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="text-sm text-gray-400">
                                No documents uploaded.
                            </p>
                        )}
                    </section>

                </div>

                <div className="flex justify-end border-t bg-gray-50 p-4">
                    <button
                        onClick={onClose}
                        className="rounded-lg border bg-white px-5 py-2.5 text-sm"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function Members() {
    const [members, setMembers] = useState([]);
    const [masters, setMasters] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [statusUpdating, setStatusUpdating] = useState(false);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [approvalFilter, setApprovalFilter] = useState("");
    const [genderFilter, setGenderFilter] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [showForm, setShowForm] = useState(false);
    const [editMember, setEditMember] = useState(null);
    const [viewMember, setViewMember] = useState(null);
    const [popup, setPopup] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [loadingDocuments, setLoadingDocuments] = useState(false);
    const [editDocuments, setEditDocuments] = useState([]);
    const [rejectModal, setRejectModal] = useState(false);
    const [rejectMemberData, setRejectMemberData] = useState(null);
    const [rejectReason, setRejectReason] = useState("");
    const [rejectLoading, setRejectLoading] = useState(false);
    const showPopup = useCallback(
        (type, title, message, onConfirm = null) => {
            setPopup({ type, title, message, onConfirm, loading: false, });
        },
        []
    );
    const closePopup = useCallback(() => {
        if (popup?.loading) return;
        setPopup(null);
    }, [popup]);

    const loadMembers = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getMembers();
            const result = unwrapMembers(response);
            setMembers(result.items.map(normalizeMember));
        } catch (error) {
            setMembers([]);
            showPopup("error", "Unable to load members", getErrorMessage(error));
        } finally {
            setLoading(false);
        }
    }, [showPopup]);

    const loadMasters = useCallback(async () => {
        try {
            const response = await getAllMemberMasters();
            setMasters(response?.data ?? response ?? {});
        } catch (error) {
            console.error("Master loading error:", error);
        }
    }, []);

    useEffect(() => {
        loadMembers();
        loadMasters();
    }, [loadMembers, loadMasters]);

    const totalMembers = members.length;
    const approvedMembers = members.filter((m) => m.approval_status === "approved").length;
    const pendingMembers = members.filter((m) => m.approval_status === "pending").length;
    const inactiveMembers = members.filter((m) => m.status === "inactive").length;
    const filteredMembers = useMemo(() => {
        const keyword = search.trim().toLowerCase();
        return members.filter((member) => {
            const matchesSearch = !keyword || [member.name, member.registration_no, member.mobile_number, member.rfId,].some((value) =>
                String(value ?? "").toLowerCase().includes(keyword));
            const matchesStatus = !statusFilter || member.status === statusFilter;
            const matchesApproval = !approvalFilter || member.approval_status === approvalFilter;
            const gender = String(member.gender_name_en ?? "").toLowerCase();
            const matchesGender = !genderFilter || gender === genderFilter;
            return (matchesSearch && matchesStatus && matchesApproval && matchesGender);
        });
    }, [members, search, statusFilter, approvalFilter, genderFilter,]);

    const totalPages = Math.max(1, Math.ceil(filteredMembers.length / pageSize));
    const safePage = Math.min(page, totalPages);
    const startIndex = (safePage - 1) * pageSize;
    const currentMembers = filteredMembers.slice(startIndex, startIndex + pageSize);
    const resetFilters = () => { setSearch(""); setStatusFilter(""); setApprovalFilter(""); setGenderFilter(""); setPage(1); };

    const handleAdd = () => {
        setEditMember(null);
        setShowForm(true);
    };

    const handleEdit = async (member) => {
        try {
            setSaving(true);
            setLoadingDocuments(true);
            const response = await getMemberById(member.rfId);
            const data = unwrapSingle(response);
            const memberData = normalizeMember(data || member);
            setEditMember(memberData);
            try {
                const docsResponse = await getMemberDocuments(member.rfId);
                setEditDocuments(unwrapDocuments(docsResponse));
            } catch (docError) {
                console.error("Edit document loading error:", docError);
                setEditDocuments([]);
            }
            setViewMember(null);
            setShowForm(true);
        } catch (error) {
            showPopup("error", "Unable to load member", getErrorMessage(error));
        } finally {
            setSaving(false);
            setLoadingDocuments(false);
        }
    };

    const handleView = async (member) => {
        try {
            setSaving(true);
            setLoadingDocuments(true);
            const response = await getMemberById(member.rfId);
            const data = unwrapSingle(response);
            setViewMember(normalizeMember(data || member));
            setDocuments([]);
            try {
                const docsResponse = await getMemberDocuments(member.rfId);
                setDocuments(unwrapDocuments(docsResponse));
            } catch (docError) {
                console.error("Document loading error:", docError);
            }
        } catch (error) {
            showPopup("error", "Unable to load profile", getErrorMessage(error));
        } finally {
            setSaving(false);
            setLoadingDocuments(false);
        }
    };

    const handleDeleteDocument = (doc) => {
        const documentId = doc.rfId;
        const memberId = viewMember?.rfId;
        if (!memberId || !documentId) {
            showPopup("error", "Delete Failed", "Member ID or document ID is missing.");
            return;
        }
        showPopup("confirm", "Delete Document", `Are you sure you want to delete ${doc.original_file_name || "this document"}?`,
            async () => {
                try {
                    await deleteMemberDocument(memberId, documentId);
                    setDocuments((previous) =>
                        previous.filter((item) => Number(item.rfId) !== Number(documentId))
                    );
                    closePopup();
                    showPopup("success", "Document Deleted", "Document deleted successfully.");
                } catch (error) {
                    closePopup();
                    showPopup("error", "Delete Failed", error.message || "Unable to delete document.");
                }
            }
        );
    };

    const handleSave = async (formData, files = {}) => {
        try {
            setSaving(true);
            let response;
            if (editMember?.rfId) {
                response = await updateMember(editMember.rfId, formData);
            } else {
                response = await createMember(formData);
            }
            const saved = unwrapSingle(response);
            const rfId = saved?.rfId ?? saved?.member?.rfId ?? saved?.item?.rfId ?? editMember?.rfId;
            const selectedFiles = Object.entries(files).filter(
                ([, file]) => file instanceof File
            );
            const uploadErrors = [];
            if (selectedFiles.length && !rfId) {
                uploadErrors.push("Member saved, but the API did not return rfId. Documents could not be uploaded.");
            } else if (rfId) {
                const results = await Promise.allSettled(
                    selectedFiles.map(([type, file]) =>
                        uploadMemberDocument(rfId, file, type)
                    )
                );
                results.forEach((result, index) => {
                    if (result.status === "rejected") {
                        uploadErrors.push(`${selectedFiles[index][0]} upload failed.`);
                    }
                });
            }
            setShowForm(false);
            setEditMember(null);
            await loadMembers();
            showPopup(
                uploadErrors.length ? "error" : "success",
                uploadErrors.length ? "Saved with warnings" : "Success",
                `${editMember ? "Member details updated successfully." : "Member registered successfully."
                }${uploadErrors.length ? "\n" + uploadErrors.join("\n") : ""
                }`
            );
        } catch (error) {
            showPopup("error", "Save failed", getErrorMessage(error));
        } finally {
            setSaving(false);
        }
    };

    const handleStatusChange = (member) => {
        if (statusUpdating) return;
        const currentStatus = String(member.status || "inactive").toLowerCase();
        const nextStatus = currentStatus === "active" ? "inactive" : "active";
        showPopup("confirm", `Change Status to ${nextStatus.toUpperCase()}?`, `Are you sure you want to change ${member.name}'s account status from ${currentStatus} to ${nextStatus}?`,
            async () => {
                if (statusUpdating) return;
                setStatusUpdating(true);
                setPopup((current) => current ? { ...current, loading: true, } : current);
                try {
                    await updateMemberStatus(member.rfId, nextStatus);
                    setMembers((current) => current.map((item) => String(item.rfId) === String(member.rfId) ? { ...item, status: nextStatus, } : item));
                    setPopup(null);
                    showPopup("success", "Status Updated", `${member.name}'s status has been changed to ${nextStatus}.`);
                    await loadMembers();
                } catch (error) {
                    setPopup(null);
                    showPopup("error", "Status Update Failed", getErrorMessage(error));
                } finally {
                    setStatusUpdating(false);
                }
            }
        );
    };

    const handleApprove = (member) => {
        showPopup("confirm", "Approve Member", `Are you sure you want to approve ${member.name}?`,
            async () => {
                closePopup();
                try {
                    const approvedBy = getUserRfId();
                    await approveMember(member.rfId, approvedBy);
                    setViewMember(null);
                    await loadMembers();
                    showPopup("success", "Member Approved", `${member.name} has been approved successfully.`);
                } catch (error) {
                    showPopup("error", "Approval Failed", getErrorMessage(error));
                }
            }
        );
    };

    const handleReject = (member) => {
        setRejectMemberData(member);
        setRejectReason(member.reject_reason || "");
        setRejectModal(true);
    };

    const confirmReject = async () => {
        if (!rejectReason.trim()) {
            showPopup("error", "Required", "Please enter a rejection reason.");
            return;
        }
        try {
            setRejectLoading(true);
            const rejectedBy = getUserRfId();
            await rejectMember(rejectMemberData.rfId, rejectReason.trim(), rejectedBy);
            setRejectModal(false);
            setRejectMemberData(null);
            setRejectReason("");
            setViewMember(null);
            await loadMembers();
            showPopup("success", "Member Rejected", `${rejectMemberData.name} has been rejected.`);
        } catch (error) {
            showPopup("error", "Rejection Failed", getErrorMessage(error));
        } finally {
            setRejectLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <div>
                    <p className="text-xs text-gray-400">Home / Members</p>
                    <h1 className="mt-2 text-2xl font-bold text-gray-800">Member Management</h1>
                    <p className="mt-1 text-sm text-gray-500">Manage member profiles, registration and approvals.</p>
                </div>
                <div className="flex gap-2">
                    <button onClick={loadMembers} disabled={loading} className="flex items-center gap-2 rounded-lg border bg-white px-4 py-3 text-sm font-semibold text-gray-600 disabled:opacity-50">
                        <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
                        Refresh
                    </button>
                    <button onClick={handleAdd} className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700">
                        <Plus size={18} />
                        Add Member
                    </button>
                </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <SummaryCard title="Total Members" value={totalMembers} icon={Users} color="blue" />
                <SummaryCard title="Approved Members" value={approvedMembers} icon={UserCheck} color="green" />
                <SummaryCard title="Pending Approval" value={pendingMembers} icon={Clock} color="amber" />
                <SummaryCard title="Inactive Members" value={inactiveMembers} icon={UserX} color="red" />
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <h2 className="mb-4 text-base font-semibold text-gray-800">Search & Filters</h2>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <div className="relative">
                        <Search size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                            placeholder="Search name, mobile or ID"
                            className="w-full rounded-lg border border-gray-200 py-2.5 pl-10 pr-3 text-sm outline-none focus:border-blue-500"
                        />
                    </div>
                    <select
                        value={genderFilter}
                        onChange={(e) => { setGenderFilter(e.target.value); setPage(1); }}
                        className="rounded-lg border border-gray-200 px-3 py-2.5 text-sm">
                        <option value="">All Genders</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                    </select>
                    <select
                        value={approvalFilter}
                        onChange={(e) => { setApprovalFilter(e.target.value); setPage(1); }}
                        className="rounded-lg border border-gray-200 px-3 py-2.5 text-sm">
                        <option value="">All Approval Status</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                    </select>
                    <select
                        value={statusFilter}
                        onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                        className="rounded-lg border border-gray-200 px-3 py-2.5 text-sm">
                        <option value="">All Account Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                    </select>
                </div>

                <div className="mt-4 flex justify-between border-t border-gray-100 pt-4">
                    <p className="text-sm text-gray-500">Total: {filteredMembers.length} members</p>
                    <button onClick={resetFilters} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800">
                        <RotateCcw size={15} />
                        Reset Filters
                    </button>
                </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                <div className="flex flex-col justify-between gap-3 border-b p-5 sm:flex-row sm:items-center">
                    <h2 className="font-semibold text-gray-800">Members List</h2>
                    <select
                        value={pageSize}
                        onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                        className="rounded-lg border border-gray-200 px-3 py-2 text-sm">
                        <option value={5}>5 Entries</option>
                        <option value={10}>10 Entries</option>
                        <option value={25}>25 Entries</option>
                        <option value={50}>50 Entries</option>
                    </select>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[1200px] text-left">
                        <thead className="bg-gray-50">
                            <tr className="text-xs uppercase text-gray-500">
                                <th className="px-5 py-4">Member</th>
                                <th className="px-4 py-4">Gender / DOB</th>
                                <th className="px-4 py-4">Mobile Number</th>
                                <th className="px-4 py-4">Location</th>
                                <th className="px-4 py-4">Approval</th>
                                <th className="px-4 py-4">Status</th>
                                <th className="px-4 py-4">Registered</th>
                                <th className="px-5 py-4 text-right">Actions</th>
                            </tr>
                        </thead>

                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                <tr>
                                    <td colSpan={8} className="py-16 text-center">
                                        <Loader2 size={25} className="mx-auto animate-spin text-blue-600" />
                                        <p className="mt-2 text-sm text-gray-500">Loading members...</p>
                                    </td>
                                </tr>
                            ) : currentMembers.length > 0 ? (
                                currentMembers.map((member) => (
                                    <tr key={member?.rfId} className="hover:bg-gray-50">
                                        <td className="px-5 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 font-semibold text-blue-600">
                                                    {member.name?.charAt(0) || "M"}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-800">{member.name}</p>
                                                    <p className="mt-1 text-xs text-blue-600">{`KVM${String(member.rfId).padStart(4, "0")}`}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-600">{member.gender_name_en || "—"}
                                            <p className="mt-1 text-xs text-gray-400">{dateOnly(member.date_of_birth)}</p>
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-600">{member.mobile_number || "—"}</td>
                                        <td className="px-4 py-4 text-sm text-gray-600">{member.city_name_en || "—"}
                                            <p className="mt-1 text-xs text-gray-400">{member.district_name_en || ""}</p>
                                        </td>
                                        <td className="px-4 py-4">
                                            <Badge color={member.approval_status === "approved" ? "green" : member.approval_status === "rejected" ? "red" : "amber"}>
                                                {member.approval_status ? member.approval_status.charAt(0).toUpperCase() + member.approval_status.slice(1) : "—"}
                                            </Badge>
                                        </td>
                                        <td className="px-4 py-4">
                                            <button type="button" onClick={() => handleStatusChange(member)} disabled={statusUpdating}
                                                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${member.status === "active" ? "bg-green-50 text-green-700 hover:bg-green-100" : "bg-red-50 text-red-700 hover:bg-red-100"}`}>
                                                <span className={`h-2 w-2 rounded-full ${member.status === "active" ? "bg-green-500" : "bg-red-500"}`} />
                                                {member.status === "active" ? "Active" : "Inactive"}
                                            </button>
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-500">{dateOnly(member.created_at)}</td>
                                        <td className="px-5 py-4">
                                            <div className="flex justify-end gap-1">
                                                <button type="button" title="View" onClick={() => handleView(member)} className="rounded-lg p-2 text-blue-600 hover:bg-blue-50">
                                                    <Eye size={17} />
                                                </button>
                                                <button type="button" title="Edit" onClick={() => handleEdit(member)} className="rounded-lg p-2 text-amber-600 hover:bg-amber-50">
                                                    <Pencil size={17} />
                                                </button>
                                                {member.approval_status !== "approved" && (
                                                    <button type="button" title="Approve Member" onClick={() => handleApprove(member)} className="rounded-lg p-2 text-green-600 hover:bg-green-50">
                                                        <CheckCircle size={17} />
                                                    </button>
                                                )}
                                                {member.approval_status !== "rejected" && (
                                                    <button type="button" title="Reject Member" onClick={() => handleReject(member)} className="rounded-lg p-2 text-red-600 hover:bg-red-50">
                                                        <XCircle size={17} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={8} className="py-16 text-center">
                                        <Users size={30} className="mx-auto text-gray-300" />
                                        <p className="mt-3 font-semibold text-gray-600">No members found</p>
                                        <p className="mt-1 text-sm text-gray-400">Try changing your filters.</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="flex flex-col justify-between gap-3 border-t border-gray-100 p-4 sm:flex-row sm:items-center">
                    <p className="text-sm text-gray-500">Showing {filteredMembers.length ? startIndex + 1 : 0}{" "}to{" "}
                        {Math.min(startIndex + pageSize, filteredMembers.length)}
                        {" "}of {filteredMembers.length} entries
                    </p>
                    <div className="flex items-center gap-2">
                        <button disabled={safePage <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg border p-2 disabled:opacity-40">
                            <ChevronLeft size={17} />
                        </button>
                        <span className="px-3 text-sm">
                            Page {safePage} of {totalPages}
                        </span>
                        <button disabled={safePage >= totalPages} onClick={() => setPage((p) => p + 1)} className="rounded-lg border p-2 disabled:opacity-40">
                            <ChevronRight size={17} />
                        </button>
                    </div>
                </div>
            </div>

            {viewMember && (
                <MemberViewModal member={viewMember} documents={documents} loadingDocuments={loadingDocuments}
                    onClose={() => setViewMember(null)} onEdit={handleEdit} onApprove={handleApprove}
                    onReject={handleReject} onDeleteDocument={handleDeleteDocument} />
            )}

            {showForm && (
                <MemberForm member={editMember} masters={masters} documents={editDocuments} saving={saving}
                    onClose={() => { setShowForm(false); setEditMember(null); setEditDocuments([]); }} onSave={handleSave} />
            )}

            <Popup popup={popup} onClose={closePopup} />

            {rejectModal && rejectMemberData && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
                    <div className="w-full max-w-md rounded-xl bg-white shadow-2xl">
                        <div className="flex items-center justify-between border-b px-6 py-4">
                            <div>
                                <h3 className="text-lg font-bold text-gray-800">Reject Member</h3>
                                <p className="mt-1 text-sm text-gray-500">Member ID: {rejectMemberData.member_id || rejectMemberData.rfId}</p>
                            </div>
                            <button type="button" onClick={() => setRejectModal(false)} disabled={rejectLoading} className="text-gray-400 hover:text-gray-700">
                                <X size={22} />
                            </button>
                        </div>
                        <div className="space-y-4 p-6">
                            <div className="rounded-lg border border-red-100 bg-red-50 p-3">
                                <p className="text-sm text-red-700">Are you sure you want to reject this member?</p>
                                <p className="mt-1 font-semibold text-gray-800">{rejectMemberData.name}</p>
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-semibold text-gray-700">Rejection Reason <span className="text-red-500">*</span></label>
                                <textarea
                                    rows={4}
                                    value={rejectReason}
                                    onChange={(e) => setRejectReason(e.target.value)}
                                    placeholder="Enter rejection reason..."
                                    className="w-full resize-none rounded-lg border border-gray-300 px-3 py-3 text-sm outline-none focus:border-red-500 focus:ring-2 focus:ring-red-100"
                                    disabled={rejectLoading} />
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 border-t px-6 py-4">
                            <button
                                type="button"
                                onClick={() => setRejectModal(false)}
                                disabled={rejectLoading}
                                className="rounded-lg border border-gray-300 px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={confirmReject}
                                disabled={rejectLoading || !rejectReason.trim()}
                                className="flex items-center gap-2 rounded-lg bg-red-600 px-5 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50">
                                {rejectLoading ? "Rejecting..." : "Confirm Reject"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function MemberForm({ member, masters, documents = [], saving, onClose, onSave, }) {
    const emptyForm = {
        name: "",
        gender_id: "",
        profile_created_by_id: "",
        date_of_birth: "",
        time_of_birth: "",
        place_of_birth: "",
        mobile_number: "",
        alternate_mobile_number: "",
        address_line1: "",
        address_line2: "",
        state_id: "",
        district_id: "",
        city_id: "",
        pin_code: "",
        native_place: "",
        father_name: "",
        mother_name: "",
        father_family_koottam_id: "",
        father_family_temple_id: "",
        father_family_temple_place_id: "",
        mother_family_koottam_id: "",
        mother_family_temple_id: "",
        mother_family_temple_place_id: "",
        number_of_siblings: 0,
        married_siblings: 0,
        highest_education_id: "",
        occupation_id: "",
        designation_id: "",
        company_name: "",
        monthly_income: "",
        work_location: "",
        family_type_id: "",
        height_id: "",
        weight_id: "",
        color_id: "",
        blood_group_id: "",
        marital_status_id: "",
        number_of_children: 0,
        family_assets: "",
        rasi_id: "",
        nakshatram_id: "",
        paadam_id: "",
        dhosam_id: "",
        referred_by: "",
        referred_by_mobile: "",
        registration_source: "admin",
    };
    const [form, setForm] = useState({
        ...emptyForm,
        ...(member || {}),
        date_of_birth: member?.date_of_birth ? String(member.date_of_birth).slice(0, 10) : "",
    });
    const [files, setFiles] = useState({});
    const [previewUrls, setPreviewUrls] = useState({});
    const [error, setError] = useState("");
    const update = (key, value) => {
        setForm((prev) => {
            const next = { ...prev, [key]: value, };
            if (key === "state_id") {
                next.district_id = "";
                next.city_id = "";
            }
            if (key === "district_id") {
                next.city_id = "";
            }
            return next;
        });
    };
    const masterOptions = [
        ["profile_created_by_id", "Profile Created By", "profile_created_by",],
        ["gender_id", "Gender", "gender",],
        ["marital_status_id", "Marital Status", "marital_status",],
        ["state_id", "State", "state",],
        ["district_id", "District", "district",],
        ["city_id", "City", "city",],
        ["father_family_koottam_id", "Father Koottam", "family_koottam",],
        ["father_family_temple_id", "Father Temple", "family_temple",],
        ["father_family_temple_place_id", "Father Temple Place", "family_temple_place",],
        ["mother_family_koottam_id", "Mother Koottam", "family_koottam",],
        ["mother_family_temple_id", "Mother Temple", "family_temple",],
        ["mother_family_temple_place_id", "Mother Temple Place", "family_temple_place",],
        ["highest_education_id", "Highest Education", "education",],
        ["occupation_id", "Occupation", "occupation",],
        ["designation_id", "Designation", "designation",],
        ["family_type_id", "Family Type", "family_type",],
        ["height_id", "Height", "height",],
        ["weight_id", "Weight", "weight",],
        ["color_id", "Color", "color",],
        ["blood_group_id", "Blood Group", "blood_group",],
        ["rasi_id", "Rasi", "rasi",],
        ["nakshatram_id", "Nakshatram", "nakshatram",],
        ["paadam_id", "Paadam", "paadam",],
        ["dhosam_id", "Dhosam", "dhosam",],
    ];

    const textFields = [
        ["name", "Full Name", "text"],
        ["date_of_birth", "Date of Birth", "date"],
        ["time_of_birth", "Time of Birth", "time"],
        ["place_of_birth", "Place of Birth", "text"],
        ["mobile_number", "Mobile Number", "tel"],
        ["alternate_mobile_number", "Alternate Mobile Number", "tel"],
        ["address_line1", "Address Line 1", "text"],
        ["address_line2", "Address Line 2", "text"],
        ["pin_code", "PIN Code", "text"],
        ["native_place", "Native Place", "text"],
        ["father_name", "Father Name", "text"],
        ["mother_name", "Mother Name", "text"],
        ["number_of_siblings", "Number of Siblings", "number"],
        ["married_siblings", "Married Siblings", "number"],
        ["company_name", "Company Name", "text"],
        ["monthly_income", "Monthly Income", "number"],
        ["work_location", "Work Location", "text"],
        ["number_of_children", "Number of Children", "number"],
        ["family_assets", "Family Assets", "text"],
        ["referred_by", "Referred By", "text"],
        ["referred_by_mobile", "Referred By Mobile", "tel"],
    ];

    const masterData = masters || {};
    const getMaster = (slug) => {
        const list = masterData[slug] ?? masterData[slug.replaceAll("-", "_")] ?? [];
        return Array.isArray(list) ? list : [];
    };
    const getId = (item) => item?.rfId ?? item?.rf_id ?? item?.id;
    const getName = (item) => item?.name_en ?? item?.name ?? item?.name_ta ?? getId(item);
    const renderInput = ([key, label, type]) => (
        <div key={key}>
            <label className="text-xs font-medium text-gray-600">{label}</label>
            <input type={type} value={form[key] ?? ""}
                max={
                    type === "date" ? new Date().toISOString().slice(0, 10) : undefined
                }
                min={
                    type === "number" ? "0" : undefined
                }
                onChange={(e) => {
                    const value = type === "tel" ? e.target.value.replace(/\D/g, "").slice(0, 10) : e.target.value;
                    update(key, value);
                }}
                className="mt-1.5 w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500"
            />
        </div>
    );

    const renderSelect = ([key, label, slug]) => {
        let options = getMaster(slug);
        if (key === "district_id" && form.state_id) {
            options = options.filter((item) => {
                const parent = item.state_id ?? item.state_rfId ?? item.parent_id;
                return (
                    parent == null ||
                    String(parent) === String(form.state_id)
                );
            });
        }
        if (key === "city_id" && form.district_id) {
            options = options.filter((item) => {
                const parent = item.district_id ?? item.district_rfId ?? item.parent_id;
                return (
                    parent == null ||
                    String(parent) === String(form.district_id)
                );
            });
        }
        return (
            <div key={key}>
                <label className="text-xs font-medium text-gray-600">{label}</label>
                <select
                    value={form[key] ?? ""}
                    onChange={(e) => update(key, e.target.value)}
                    disabled={
                        (key === "district_id" && !form.state_id) ||
                        (key === "city_id" && !form.district_id)
                    }
                    className="mt-1.5 w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500 disabled:bg-gray-50">
                    <option value="">Select {label}</option>
                    {options.map((item) => (
                        <option key={getId(item)} value={getId(item)}>
                            {getName(item)}
                            {item.name_ta ? ` / ${item.name_ta}` : ""}
                        </option>
                    ))}
                </select>
            </div>
        );
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setError("");
        if (!form.name.trim()) {
            setError("Please enter member name.");
            return;
        }
        if (!/^\d{10}$/.test(String(form.mobile_number))) {
            setError("Enter a valid 10-digit mobile number.");
            return;
        }
        if (!form.gender_id) {
            setError("Please select gender.");
            return;
        }
        if (!form.date_of_birth) {
            setError("Please select date of birth.");
            return;
        }
        const dob = new Date(
            `${form.date_of_birth}T00:00:00`
        );
        const today = new Date();
        if (dob > today || Number.isNaN(dob.getTime())) {
            setError("Please enter a valid date of birth.");
            return;
        }
        const age =
            today.getFullYear() -
            dob.getFullYear() -
            (
                today.getMonth() < dob.getMonth() || (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate()) ? 1 : 0
            );
        if (age < 18) {
            setError("Member must be at least 18 years old.");
            return;
        }
        if (
            form.alternate_mobile_number &&
            !/^\d{10}$/.test(String(form.alternate_mobile_number))
        ) {
            setError("Enter a valid 10-digit alternate mobile number.");
            return;
        }
        if (
            form.referred_by_mobile &&
            !/^\d{10}$/.test(String(form.referred_by_mobile))
        ) {
            setError("Enter a valid 10-digit referral mobile number.");
            return;
        }
        onSave(form, files);
    };

    return (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-2 sm:p-5">
            <div className="flex max-h-[96vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white">
                <div className="flex items-center justify-between border-b p-5">
                    <div>
                        <h2 className="text-lg font-bold text-gray-800">{member ? "Edit Member" : "Add New Member"}</h2>
                        <p className="mt-1 text-xs text-gray-500">Enter member registration details.</p>
                    </div>
                    <button type="button" onClick={onClose} disabled={saving} className="rounded-lg p-2 hover:bg-gray-100 disabled:opacity-50">
                        <X size={20} />
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="flex-1 space-y-7 overflow-y-auto p-5">
                    <section>
                        <h3 className="mb-4 font-semibold text-gray-800">Personal Information</h3>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {textFields.slice(0, 6).map(renderInput)}
                            {masterOptions.slice(0, 3).map(renderSelect)}
                        </div>
                    </section>
                    <section className="border-t pt-5">
                        <h3 className="mb-4 font-semibold text-gray-800">Address Information</h3>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {textFields.slice(6, 10).map(renderInput)}
                            {masterOptions.slice(3, 6).map(renderSelect)}
                        </div>
                    </section>
                    <section className="border-t pt-5">
                        <h3 className="mb-4 font-semibold text-gray-800">Family Information</h3>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {textFields.slice(10, 14).map(renderInput)}
                            {masterOptions.slice(6, 12).map(renderSelect)}
                        </div>
                    </section>
                    <section className="border-t pt-5">
                        <h3 className="mb-4 font-semibold text-gray-800">Education & Career</h3>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {masterOptions.slice(12, 15).map(renderSelect)}
                            {textFields.slice(14, 17).map(renderInput)}
                        </div>
                    </section>
                    <section className="border-t pt-5">
                        <h3 className="mb-4 font-semibold text-gray-800">Physical & Horoscope Details</h3>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {masterOptions.slice(15).map(renderSelect)}
                            {textFields.slice(17).map(renderInput)}
                        </div>
                    </section>
                    <section className="border-t pt-5">
                        <h3 className="mb-4 font-semibold text-gray-800">Documents & Image Preview</h3>
                        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                            {[
                                ["profile_picture", "Profile Picture", "image/*"],
                                ["horoscope_copy", "Horoscope Copy", ".pdf,image/*"],
                                ["community_certificate", "Community Certificate", ".pdf,image/*"],
                                ["divorce_certificate", "Divorce Certificate", ".pdf,image/*"],
                            ].map(([key, label, accept]) => {
                                const existingDoc = documents.find((doc) => doc.document_type === key);
                                const selectedFile = files[key];
                                const existingUrl = existingDoc ? getDocumentUrl(existingDoc) : "";
                                const previewUrl = previewUrls[key] || existingUrl;
                                const showImage = selectedFile ? selectedFile.type.startsWith("image/") : existingDoc ? isImageDocument(existingDoc) : false;
                                return (
                                    <div key={key} className="rounded-xl border border-gray-200 p-4">
                                        <label className="text-sm font-semibold text-gray-700">{label}</label>
                                        {previewUrl && showImage && (
                                            <div className="mt-3 overflow-hidden rounded-lg border bg-gray-50">
                                                <img src={previewUrl} alt={label} className="h-44 w-full object-contain" />
                                            </div>
                                        )}
                                        {previewUrl && !showImage && (
                                            <div className="mt-3 flex items-center gap-3 rounded-lg bg-gray-50 p-4">
                                                <FileText size={35} className="text-red-500" />
                                                <div className="min-w-0 flex-1">
                                                    <p className="truncate text-sm font-semibold text-gray-700">
                                                        {selectedFile?.name ||
                                                            existingDoc?.original_name ||
                                                            existingDoc?.file_name ||
                                                            "Uploaded Document"}
                                                    </p>
                                                    <p className="text-xs text-gray-400">PDF / Document</p>
                                                </div>
                                                {existingUrl && !selectedFile && (
                                                    <a href={existingUrl} target="_blank" rel="noreferrer" className="text-blue-600">
                                                        <ExternalLink size={18} />
                                                    </a>
                                                )}
                                            </div>
                                        )}
                                        {!previewUrl && (
                                            <div className="mt-3 flex h-28 items-center justify-center rounded-lg border border-dashed bg-gray-50">
                                                <p className="text-xs text-gray-400">No document uploaded</p>
                                            </div>
                                        )}
                                        <input type="file" accept={accept}
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (!file) return;
                                                if (file.size > 10 * 1024 * 1024) {
                                                    setError("File size must be below 10 MB.");
                                                    e.target.value = "";
                                                    return;
                                                }
                                                if (previewUrls[key]?.startsWith("blob:")) {
                                                    URL.revokeObjectURL(previewUrls[key]);
                                                }
                                                setFiles((prev) => ({ ...prev, [key]: file, }));
                                                setPreviewUrls((prev) => ({ ...prev, [key]: URL.createObjectURL(file), }));
                                                setError("");
                                            }}
                                            className="mt-3 block w-full rounded-lg border border-gray-200 p-2 text-xs" />
                                        {selectedFile && (
                                            <p className="mt-2 text-xs text-green-600">New file selected: {selectedFile.name}</p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                    {error && (
                        <p className="rounded-lg bg-red-50 p-3 text-sm text-red-600">{error}</p>
                    )}
                    <div className="flex justify-end gap-3 border-t pt-5">
                        <button type="button" onClick={onClose} disabled={saving} className="rounded-lg border px-5 py-2.5 text-sm disabled:opacity-50">
                            Cancel
                        </button>
                        <button type="submit" disabled={saving} className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60">
                            {saving && (
                                <Loader2 size={16} className="animate-spin" />
                            )}
                            {saving ? "Saving..." : member ? "Update Member" : "Save Member"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}